/**
 * N2 · S7b · Authorise the card fee — the payment screen, on its own.
 *
 * This screen owns the Stripe seam and owns the single submit path. The review
 * summary lives on /create/pay; nothing on this screen re-renders it, so there is
 * no second implementation of the order summary and no second submit.
 *
 * MODEL: authorise, don't capture. The amount is resolved SERVER-SIDE from the
 * card fee alone; this client never sends an amount, and the intended load
 * travels as metadata only.
 *
 * LEGAL GATES (L1): raw card details never touch this project — the Payment
 * Element is Stripe's iframe. No scheme mark or name is rendered.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toCssColour } from "@/lib/color";
import { submitCard } from "@/services/card-requests";
import { bindCardRequestAttributionMonitored } from "@/services/funnel-analytics";
import {
  readDetails,
  readGuestToken,
  readSelectedVariant,
  readStylePick,
  recordFunnelEvent,
  saveDetails,
} from "@/lib/consumer-session";
import { useConsumerFlow } from "../ConsumerFlow";
import {
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  PrimaryButton,
  StickyBar,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S7_SECTION, S7_DEFAULTS } from "@/content/screens/s7";
import { normaliseQuantity, resolveOrderPrice, resolvePricing } from "@/lib/consumer-config";
import { formatMoney } from "@/lib/consumer-config";
import { CardFeePayment, type PaymentPhase } from "@/components/consumer/CardFeePayment";
import { currentPaymentAttempt, resetPaymentAttempt } from "@/lib/payment-attempt";
import { getMyDiscountQuote, type DiscountQuote } from "@/services/referrals";

type Phase = "review" | "submitting" | "done" | "error";

export default function S7bAuthorise() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const { t } = useSiteContent(S7_SECTION, S7_DEFAULTS);
  const [phase, setPhase] = useState<Phase>("review");
  const [error, setError] = useState<string | null>(null);
  const [, setPaymentPhase] = useState<PaymentPhase>("loading");
  const attemptId = useMemo(() => currentPaymentAttempt(program.slug), [program.slug]);

  const variant = readSelectedVariant(program.slug);
  const details = readDetails(program.slug);
  const pick = readStylePick(program.slug);
  const cfg = (program.config ?? {}) as Record<string, any>;
  // Pass 3a (2026-09-08): there is ONE checkout. "Waitlist" is a routing
  // OUTCOME decided server-side at payment (authorise_and_allocate_lead →
  // route_lead), not a screen variant. The old `cfg.tenant_type === "waitlist"`
  // branch swapped the fee pre-authorisation for a dead "Join the waitlist"
  // button that threw (B5 seam) — removed. Whether the lead is held or goes
  // to a bank is visible to the consumer on S8/S9b from the server's answer.

  const pricing = resolvePricing(cfg, program.currency);
  /* The quantity chosen on the review screen, re-validated against configured
     tiers. The AMOUNT is still resolved server-side from this quantity; this
     client sends no amount and never multiplies a fee. */
  const quantity = normaliseQuantity(pricing, details?.card_quantity ?? 1);
  const orderPrice = resolveOrderPrice(pricing, quantity);
  const total = orderPrice.cents; // fee ONLY — never `+ load`.
  const intendedLoadCents = details?.intended_load_amount_cents ?? null;
  const [quote, setQuote] = useState<DiscountQuote | null>(null);
  // The figure authorised: the server quote when it resolves, else the list fee.
  const dueCents = quote ? quote.amountCents : (total ?? 0);

  useEffect(() => {
    if (!variant || !details) navigate("../name", { replace: true });
  }, [variant, details, navigate]);

  useEffect(() => {
    let live = true;
    getMyDiscountQuote(program.program_id, quantity).then(({ data }) => {
      if (live && data) setQuote(data);
    });
    return () => {
      live = false;
    };
  }, [program.program_id, quantity]);


  async function onSubmit(paymentIntentId?: string) {
    if (!variant || !details) return;
    setPhase("submitting");
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getUser();
      if (!sess?.user) throw new Error(t("s7.error.notSignedIn"));

      if (paymentIntentId) {
        recordFunnelEvent(program.slug, "payment_authorised", program.program_id);
      }

      const res = await submitCard({
        programId: program.program_id,
        /* A REORDER prefills from a stored row, which may hold bare HSL
           components ("222 69% 46%"). Normalising here means the new order is
           saved as a paintable value instead of propagating the bad one. */
        cardColor:
          toCssColour(details.card_color) ||
          toCssColour(variant.card_color) ||
          program.primary_color ||
          "#111111",
        orientation: details.orientation || variant.orientation || "landscape",
        style: pick?.style_id || "",
        styleSubChoice: pick?.sub_style_id ?? null,
        hobby: pick?.hobby_id ?? null,
        firstName: details.first_name,
        middleName: details.middle_name ?? null,
        lastName: details.last_name,
        phone: details.phone ?? null,
        // KYC/OFAC identity only. Not an age signal.
        dateOfBirth: details.date_of_birth ?? null,
        panLast5: details.pan_last5 ?? null,
        deliveryType: details.delivery_type,
        branchId: details.branch_id ?? null,
        postalAddress: details.postal_address ?? null,
        selectedAvatarImageId: variant.generation_id,
        avatarPosition: variant.avatar_position ?? { x: 50, y: 50 },
        avatarScale: variant.avatar_scale ?? 1,
        // Captured preference for the ISSUING BANK — never a Cardmoji charge.
        intendedLoadAmountCents: intendedLoadCents,
        quantity,
        channel: "consumer",
        paymentIntentId: paymentIntentId ?? null,
      });

      if (res.error) throw res.error;
      /* The LAST stage of the table, fired from the live path. The three older
         rows came from a path that no longer exists, so until now the final
         decrement could never be read. */
      recordFunnelEvent(program.slug, "submitted", program.program_id);
      const refNumber =
        (res.data as any)?.ref_number ?? (res.data as any)?.cardRequest?.ref_number ?? null;

      const requestId =
        (res.data as any)?.card_request_id ??
        (res.data as any)?.id ??
        (res.data as any)?.cardRequest?.id ??
        null;
      if (requestId) {
        // Fire-and-forget so it cannot fail the payment, but NOT silent: every
        // failure is logged and recorded in the miss ledger the sweep pages on.
        bindCardRequestAttributionMonitored({
          cardRequestId: requestId,
          guestToken: readGuestToken(program.slug),
          quantity,
          tierLabel: orderPrice.tierLabel ?? null,
        });
      }


      setPhase("done");
      resetPaymentAttempt(program.slug);
      saveDetails(program.slug, { ...details, first_name: "", last_name: "" } as any);
      navigate("../confirmed", { replace: true, state: { ref: refNumber } });
    } catch (e: any) {
      setError(e?.message || String(e));
      setPhase("error");
    }
  }

  if (!variant || !details) return null;

  return (
    <FunnelPage testId="s7b-authorise">
      <FunnelContainer size="form">
        <FunnelHeader backTo="../pay" backLabel={t("s7.header.backLabel")} />

        <Display className="mb-[22px] max-w-[20ch]">{t("s7.title")}</Display>

        {/* L11 · HANDOFF DISCLOSURE. Sits immediately before the submit path —
            above the Payment Element and its pinned CTA — never collapsed,
            never behind a link, because counsel's condition for the generic
            form is that it is visible at the point of submission. The category
            named here ("a participating bank") is the same disclosure the
            allocation email later resolves to an instance (L13).
            NOT YET A RECORDED CONSENT: continuing is the affirmative act, but
            nothing durable proves WHICH text was shown. That becomes a register
            acceptance (doc-key + version + body hash, obtained_via
            'consent_step') once L2 lands — until then any consent captured here
            has exactly the unprovability problem of the 21 held leads.
            SCOPE (BINDING): this sentence MUST become a Legal Register document
            from the moment the register is importable, so the text shown here
            carries a pinned version and hash. A reviewed sentence shown to
            customers with nothing versioned behind it is precisely the defect
            the register closes; it must not survive the import. */}
        <p
          className="mb-[22px] rounded-[14px] bg-foreground/[0.04] p-3 text-[13px] leading-[1.55] text-foreground/70"
          data-testid="s7-handoff-disclosure"
        >
          {t("s7.legal.handoff")}
        </p>

        {total !== null && (
          <div data-testid="s7-payment">
            {/* B7 · The figures are the SERVER quote, never local arithmetic.
                Until it resolves the list fee stands, which is the pre-referral
                behaviour; once it resolves, the fee/discount/due lines and the
                pinned CTA all read the one number Stripe is given. */}
            {quote && quote.discountCents > 0 && (
              <dl className="mb-4 space-y-1 text-[14px]" data-testid="s7-price-breakdown">
                <div className="flex justify-between">
                  <dt className="text-foreground/70">
                    {t("s7.price.feeLine").replace("{fee}", formatMoney(quote.feeCents, pricing.currency))}
                  </dt>
                </div>
                <div className="flex justify-between text-[#1A6B42]">
                  <dt>
                    {t("s7.price.discountLine").replace(
                      "{discount}",
                      formatMoney(quote.discountCents, pricing.currency),
                    )}
                  </dt>
                </div>
                <div className="flex justify-between font-semibold text-foreground">
                  <dt>
                    {t("s7.price.dueLine").replace("{due}", formatMoney(dueCents, pricing.currency))}
                  </dt>
                </div>
              </dl>
            )}
            <CardFeePayment
              programId={program.program_id}
              attemptId={attemptId}
              intendedLoadAmountCents={intendedLoadCents}
              quantity={quantity}
              submitting={phase === "submitting"}
              onPhaseChange={setPaymentPhase}
              buttonLabel={t("s7.footer.preAuthorize").replace("{amount}", formatMoney(dueCents, pricing.currency))}
              confirmingLabel={t("s7.footer.submitting")}
              authorisedLabel={t("s7.footer.placingOrder")}
              timeoutMessage={t("s7.error.confirmTimeout")}
              securityNotice={t("s7.security.notice")}
              onAuthorised={(intentId) => onSubmit(intentId)}
            />
          </div>
        )}


        {error && (
          <p className="mt-4 text-[14px] font-medium text-[#C41112]" data-testid="s7-pay-error">
            {error}
          </p>
        )}
      </FunnelContainer>

      {total === null && (
        <StickyBar>
          <PrimaryButton
            onClick={() => onSubmit()}
            disabled={phase === "submitting"}
            data-testid="s7-submit"
            className="basis-full"
          >
            {phase === "submitting"
              ? t("s7.footer.submitting")
              : t("s7.footer.submitBank")}
          </PrimaryButton>
        </StickyBar>
      )}
    </FunnelPage>
  );
}
